/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import type { AuditLogEventActor } from '@atlassian/audit-logs-common';

import Avatar from '../../../../../common/utils/components/avatar';
import AvatarItem from '../../../../../common/utils/components/avatar-item';
import { DetailsRow } from '../../../../../common/utils/components/details-row';
import { DetailsSection } from '../../../../../common/utils/components/details-section';

import { UserProfileLink } from './user-profile-link';
import { getProductLogo, isSystemActor } from './utils';

const messages = defineMessages({
	actor: {
		id: 'organization.audit.log.side-panel.tab-panels.details.actor-section.title',
		defaultMessage: 'Actor',
		description: 'Title for the actor section in audit log details panel',
	},
	name: {
		id: 'organization.audit.log.side-panel.tab-panels.details.actor-section.name.label',
		defaultMessage: 'Name',
		description: 'Label for the actor name field in audit log actor section',
	},
	email: {
		id: 'organization.audit.log.side-panel.tab-panels.details.actor-section.email.label',
		defaultMessage: 'Email',
		description: 'Label for the actor email field in audit log actor section',
	},
	accountId: {
		id: 'organization.audit.log.side-panel.tab-panels.details.actor-section.account-id.label',
		defaultMessage: 'Account ID',
		description: 'Label for the actor account ID field in audit log actor section',
	},
	userProfile: {
		id: 'organization.audit.log.side-panel.tab-panels.details.actor-section.user-profile.label',
		defaultMessage: 'Details',
		description: 'Label for the user profile link field in audit log actor section',
	},
});

interface ActorSectionProps {
	actor?: AuditLogEventActor;
}

export const ActorSection = ({ actor }: ActorSectionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();

	if (!actor) {
		return null;
	}

	const { name, avatarUrl, email, accountId, profileLink } = actor;

	// Determine if this is a system actor and get appropriate avatar content
	const isSystem = isSystemActor(actor);
	const avatarContent = isSystem ? getProductLogo(actor) : <Avatar size="small" src={avatarUrl} />;

	return (
		<DetailsSection title={formatMessage(messages.actor)}>
			{name && (
				<DetailsRow
					label={formatMessage(messages.name)}
					value={<AvatarItem primaryText={name} avatar={avatarContent} />}
				/>
			)}
			{email && <DetailsRow label={formatMessage(messages.email)} value={email} />}
			{accountId && <DetailsRow label={formatMessage(messages.accountId)} value={accountId} />}
			{profileLink && (
				<DetailsRow
					label={formatMessage(messages.userProfile)}
					value={<UserProfileLink profileUrl={profileLink} />}
				/>
			)}
		</DetailsSection>
	);
};
